import { useEffect, useState } from "react";
import Modal from "react-modal";
import { BsStars } from "react-icons/bs";
import { toast } from "react-toastify";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import React from "react";

// Components
import DarkBlueButton from "../../atoms/buttons/DarkBlueButton";
import WhiteButton from "../../atoms/buttons/WhiteButton";
import CloseButton from "../../atoms/buttons/CloseButton";
import PrescriptionPdf from "../../../screens/PrescriptionPdf";

// Types
import { Doctor, Hospital } from "../../../lib/utils/types";
import { BookingData } from "../../../services/types";

// Utils and APIs
import { getRouteSegment } from "../../../lib/utils/funcs";
import { getHosptialDetails } from "../../../lib/apis/hospital";
import { getDoctorAvailability } from "../../../lib/apis/doctor";
import {
  createPrescriptionTemplate,
  getPrescriptionTemplatesByTemplateId,
  updatePrescriptionTemplate,
  generatePrescriptionWithAI,
} from "../../../lib/apis/prescription";
import { storage } from "../../../firebase";
import {
  getMedicines,
  insertMedicine,
  searchMedicines,
} from "../../../lib/apis/medicines";

// Constants
const DEFAULT_FORM_DATA = {
  complaints: "",
  template_name: "",
  historyAndExaminations: "",
  diagnosis: "",
  investigations: "",
  previousMedicines: "",
  medicines: [
    {
      name: "",
      dosage: "",
      duration: "",
    },
  ],
  vitals: {
    bp: "",
    weight: "",
    pulse: "",
    temperature: "",
  },
  advice: "",
  signature: "",
};

const FORM_FIELDS = [
  {
    name: "complaints",
    label: "Chief Complaints",
    placeholder: "e.g., Fever for 3 days, persistent headache",
  },
  {
    name: "historyAndExaminations",
    label: "History & Examinations",
    placeholder:
      "e.g., Patient reports similar episodes. On examination, temp 101°F, mild dehydration.",
  },
  {
    name: "diagnosis",
    label: "Diagnosis",
    placeholder: "e.g., Viral Fever, Acute Gastritis",
  },
  {
    name: "investigations",
    label: "Investigations",
    placeholder: "e.g., CBC, Blood Sugar, X-Ray Chest PA view",
  },
  {
    name: "previousMedicines",
    label: "Previous Medicines",
    placeholder: "e.g., Paracetamol 500mg (SOS for fever)",
  },
] as const;

interface Medicine {
  name: string;
  dosage: string;
  duration: string;
}

interface TemplateData {
  complaints: string;
  historyAndExaminations: string;
  diagnosis: string;
  investigations: string;
  previousMedicines: string;
  medicines: Medicine[];
  advice: string;
  signature: string;

  template_name: string;
}

interface CreatePrescriptionModalProps {
  closeModal: () => void;
  modalIsOpen: boolean;
  customStyles: any;
  doctorId: string | null;
  templateId: string | null;
  type: string;
  mappingId: string | null;
}

const CreatePrescriptionModal: React.FC<CreatePrescriptionModalProps> = ({
  closeModal,
  modalIsOpen,
  customStyles,
  doctorId,
  templateId,
  type,
  mappingId,
}) => {
  const hospital_id = getRouteSegment(1);

  // State management
  const [hospData, setHospData] = useState<Hospital>();
  const [preview, setPreview] = useState(false);
  const [bookings, setBookings] = useState<BookingData>();
  const [docDetails, setDocDetails] = useState<Doctor>();
  const [signatureFile, setSignatureFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [signaturePreviewUrl, setSignaturePreviewUrl] = useState<string | null>(
    null
  );
  const [isUploadingSignature, setIsUploadingSignature] = useState(false);
  const [formData, setFormData] = useState<TemplateData>(DEFAULT_FORM_DATA);
  const [aiPrompt, setAiPrompt] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [search, setSearch] = useState("");
  const [searchingIndex, setSearchingIndex] = useState<number | null>(null);
  const [medicineNames, setMedicineNames] = useState([{ product_name: "" }]);
  const [searchResults, setSearchResults] = useState<
    { product_name: string }[]
  >([]);
  const [isSearching, setIsSearching] = useState(false);

  // Add debounced search effect
  useEffect(() => {
    const searchTimeout = setTimeout(async () => {
      if (search && searchingIndex !== null) {
        setIsSearching(true);
        try {
          const res = await searchMedicines(search);
          if (res?.status === 200) {
            setSearchResults(res.data.result);
          }
        } catch (error) {
          console.error("Error searching medicines:", error);
        } finally {
          setIsSearching(false);
        }
      } else {
        setSearchResults([]);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(searchTimeout);
  }, [search, searchingIndex]);

  const resetForm = () => {
    setFormData(DEFAULT_FORM_DATA);
    setPreview(false);
    setSignatureFile(null);
    setSignaturePreviewUrl(null);
    setAiPrompt("");
    setSearch("");
  };

  const handleClose = () => {
    resetForm();
    closeModal();
  };

  const uploadSignatureToFirebase = async (file: File): Promise<string> => {
    setIsUploadingSignature(true);
    try {
      const storageRef = ref(storage, `signatures/${Date.now()}-${file.name}`);
      const uploadTask = uploadBytesResumable(storageRef, file);
      await uploadTask;
      const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
      setFormData((prev) => ({ ...prev, signature: downloadURL }));
      return downloadURL;
    } catch (error) {
      console.error("Error uploading signature:", error);
      throw error;
    } finally {
      setIsUploadingSignature(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [hospData, doctorData] = await Promise.all([
          getHosptialDetails(hospital_id),
          getDoctorAvailability(mappingId || ""),
          templateId ? getPrescriptionTemplatesByTemplateId(templateId) : null,
        ]);

        if (hospData?.status === 200) {
          setHospData(hospData.data.result);
        }

        if (doctorData?.status === 200) {
          setDocDetails(doctorData.data.result.doctor_details);
        }

        if (
          ((modalIsOpen && type === "edit") ||
            (modalIsOpen && type === "view")) &&
          templateId
        ) {
          const [templateData] = await Promise.all([
            getPrescriptionTemplatesByTemplateId(templateId),
          ]);
          if (templateData?.status === 200) {
            const template = templateData.data.result;
            setFormData({
              complaints: template.data.complaints || "",
              diagnosis: template.data.diagnosis || "",
              investigations: template.data.investigations || "",
              previousMedicines: template.data.previousMedicines || "",
              historyAndExaminations:
                template.data.historyAndExaminations || "",
              advice: template.data.advice || "",
              medicines: template.data.medicines || [],
              signature: template.data.signature || "",
              template_name: template.template_name || "",
            });
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Failed to load data");
      }
    };
    fetchData();
  }, [hospital_id, mappingId, templateId, modalIsOpen]);

  useEffect(() => {
    if (formData.signature && !signatureFile) {
      setSignaturePreviewUrl(formData.signature);
    } else if (!formData.signature && !signatureFile) {
      setSignaturePreviewUrl(null);
    }
  }, [formData.signature, signatureFile]);

  const handleSignatureFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    if (!file) {
      setSignatureFile(null);
      if (!formData.signature) {
        setSignaturePreviewUrl(null);
      }
      return;
    }

    setSignatureFile(file);
    const previewUrl = URL.createObjectURL(file);
    setSignaturePreviewUrl(previewUrl);

    try {
      await uploadSignatureToFirebase(file);
    } catch (error) {
      toast.error("Failed to upload signature");
      setSignatureFile(null);
      setSignaturePreviewUrl(formData.signature);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    medicineIndex?: number
  ) => {
    const { name, value } = e.target;

    if (medicineIndex !== undefined) {
      setFormData((prev) => {
        const updatedMedicines = [...prev.medicines];
        updatedMedicines[medicineIndex] = {
          ...updatedMedicines[medicineIndex],
          [name]: value,
        };
        return { ...prev, medicines: updatedMedicines };
      });

      // Update search state when medicine name is being edited
      if (name === "name") {
        setSearch(value);
        setSearchingIndex(medicineIndex);
      }
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const addMedicine = () => {
    setFormData((prev) => ({
      ...prev,
      medicines: [...prev.medicines, { name: "", dosage: "", duration: "" }],
    }));
  };

  const removeMedicine = (indexToRemove: number) => {
    setFormData((prev) => ({
      ...prev,
      medicines: prev.medicines.filter((_, index) => index !== indexToRemove),
    }));
  };

  const handleGenerate = async () => {
    if (!aiPrompt.trim()) return;
    setIsGenerating(true);

    try {
      const res = await generatePrescriptionWithAI(formData, aiPrompt);

      if (res?.status === 200) {
        const aiResult: Partial<TemplateData> = res.data.result;
        const completeFormData: TemplateData = {
          ...DEFAULT_FORM_DATA,
          template_name: formData.template_name || "",
          signature: formData.signature || "",
          ...aiResult,
          medicines: aiResult.medicines ?? DEFAULT_FORM_DATA.medicines,
        };
        setFormData(completeFormData);
      } else {
        toast.error("Failed to generate prescription");
      }
    } catch (err) {
      console.error("AI generation error:", err);
      toast.error("Failed to generate prescription");
    } finally {
      setAiPrompt("");
      setIsGenerating(false);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    if (!formData.signature || formData.signature.trim() === "") {
      toast.error("Add and upload doctor's signature first.");
      setLoading(false);
      return;
    }
    try {
      // Create a Set of existing medicine names for O(1) lookup
      const existingMedicineNames = new Set(
        medicineNames.map((m) => m.product_name)
      );

      // Filter out medicines that need to be inserted
      const medicinesToInsert = formData.medicines.filter(
        (med) => !existingMedicineNames.has(med.name)
      );

      // Insert all new medicines in parallel
      if (medicinesToInsert.length > 0) {
        await Promise.all(
          medicinesToInsert.map((med) => insertMedicine(med.name))
        );
      }
      const { template_name, ...formDataWithoutTemplate } = formData;
      const res = await createPrescriptionTemplate(
        template_name,
        doctorId || "",
        JSON.stringify(formDataWithoutTemplate)
      );

      if (res.status === 200 || res.status === 201) {
        toast.success("Template Created");
        handleClose();
      } else {
        toast.error(res.error || "Failed to create template");
      }
    } catch (error) {
      console.error("Error saving template:", error);
      toast.error("Failed to save template");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    setLoading(true);
    try {
      // Create a Set of existing medicine names for O(1) lookup
      const existingMedicineNames = new Set(
        medicineNames.map((m) => m.product_name)
      );

      // Filter out medicines that need to be inserted
      const medicinesToInsert = formData.medicines.filter(
        (med) => !existingMedicineNames.has(med.name)
      );

      // Insert all new medicines in parallel
      if (medicinesToInsert.length > 0) {
        await Promise.all(
          medicinesToInsert.map((med) => insertMedicine(med.name))
        );
      }
      const { template_name, ...formDataWithoutTemplate } = formData;
      const res = await updatePrescriptionTemplate(
        templateId || "",
        template_name,
        doctorId || "",
        JSON.stringify(formDataWithoutTemplate)
      );

      if (res.status === 200 || res.status === 201) {
        toast.success("Template Updated");
        handleClose();
      } else {
        toast.error(res.error || "Failed to update template");
      }
    } catch (error) {
      console.error("Error updating template:", error);
      toast.error("Failed to update template");
    } finally {
      setLoading(false);
    }
  };

  const isView = type === "view";
  const isSmallScreen =
    typeof window !== "undefined" && window.innerWidth < 1024; // Changed to lg breakpoint
  const [smallScreenPreview, setSmallScreenPreview] = useState(false);

  return (
    <Modal
      isOpen={modalIsOpen}
      onRequestClose={handleClose}
      style={customStyles}
      ariaHideApp={false}
    >
      {/* Header */}
      <div className="px-6 py-3 flex flex-row items-center justify-between border-b-[0.5px] border-sbBorder ">
        <p className="text-dark text-md font-semibold">
          {type === "create"
            ? "Create Prescription"
            : type === "view"
            ? "View Prescription"
            : "Edit Prescription"}
        </p>
        <CloseButton handleClick={handleClose} />
      </div>
      {isGenerating && (
        <div className="absolute inset-0 bg-black opacity-40">
          <div className="flex items-center justify-center h-full">
            <div className="p-4 rounded-lg shadow-lg">
              <img
                src="https://ik.imagekit.io/ok2wgebfs/Animation%20-%201746713902950%20(1).gif?updatedAt=1746715856238"
                alt=""
                className="w-60"
              />
            </div>
          </div>
        </div>
      )}
      <div className="w-full bg-white rounded-lg shadow-lg">
        <div className="flex flex-col lg:flex-row">
          {isSmallScreen && isView ? (
            !smallScreenPreview ? (
              <div className="w-full flex flex-col h-[calc(100vh-120px)]">
                <div className="flex-1 overflow-y-auto p-4">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">
                        Template Name
                      </label>
                      <input
                        type="text"
                        id="template_name"
                        name="template_name"
                        className="mt-1 w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="eg.cough,cold etc"
                        value={formData.template_name}
                        onChange={handleInputChange}
                        disabled={true}
                      />
                    </div>
                    {FORM_FIELDS.map((field) => (
                      <div key={field.name} className="mb-4">
                        <label
                          htmlFor={field.name}
                          className="block text-sm font-medium text-gray-700 mb-1"
                        >
                          {field.label}
                        </label>
                        <textarea
                          id={field.name}
                          name={field.name}
                          value={
                            formData[
                              field.name as keyof Omit<
                                TemplateData,
                                "medicines" | "vitals" | "header"
                              >
                            ]
                          }
                          onChange={handleInputChange}
                          className="mt-1 w-full p-2 border border-gray-300 rounded-md h-20 shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                          placeholder={field.placeholder}
                          disabled={true}
                        />
                      </div>
                    ))}
                    {/* Medicines Section */}
                    <div className="mb-4">
                      <label className="block text-lg font-semibold text-gray-800 mb-2">
                        Medicines
                      </label>
                      {formData.medicines.map((medicine, index) => (
                        <div
                          key={index}
                          className="p-3 border border-gray-200 rounded-md mb-3 bg-gray-50 shadow-sm"
                        >
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-2">
                            <div>
                              <label
                                htmlFor={`medicine_name_${index}`}
                                className="block text-xs font-medium text-gray-600 mb-1"
                              >
                                Name
                              </label>
                              <input
                                type="text"
                                id={`medicine_name_${index}`}
                                name="name"
                                value={medicine.name}
                                onChange={(e) => handleInputChange(e, index)}
                                className="mt-1 w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                                placeholder="e.g., Azithromycin"
                                disabled={true}
                              />
                            </div>
                            <div>
                              <label
                                htmlFor={`medicine_dosage_${index}`}
                                className="block text-xs font-medium text-gray-600 mb-1"
                              >
                                Dosage
                              </label>
                              <input
                                type="text"
                                id={`medicine_dosage_${index}`}
                                name="dosage"
                                value={medicine.dosage}
                                onChange={(e) => handleInputChange(e, index)}
                                className="mt-1 w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                                placeholder="1-0-1 / 5ml"
                                disabled={true}
                              />
                            </div>
                            <div>
                              <label
                                htmlFor={`medicine_duration_${index}`}
                                className="block text-xs font-medium text-gray-600 mb-1"
                              >
                                Duration
                              </label>
                              <input
                                type="text"
                                id={`medicine_duration_${index}`}
                                name="duration"
                                value={medicine.duration}
                                onChange={(e) => handleInputChange(e, index)}
                                className="mt-1 w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                                placeholder="e.g. 5 days , 2 weeks"
                                disabled={true}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    {/* Advice Textarea */}
                    <div className="mb-4">
                      <label
                        htmlFor="advice"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Advice
                      </label>
                      <textarea
                        id="advice"
                        name="advice"
                        value={formData.advice}
                        onChange={handleInputChange}
                        className="mt-1 w-full p-2 border border-gray-300 rounded-md h-20 shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder="e.g., Take complete rest. Drink plenty of fluids. Follow up after 3 days."
                        disabled={true}
                      />
                    </div>
                    {/* Disclaimer */}
                    <p className="mt-1 text-xs text-gray-500">
                      <span className="font-semibold">Note:</span> To make words
                      bold, wrap them in a single asterisk like{" "}
                      <code>*example*</code>. For a new line, press{" "}
                      <span className="font-semibold">Enter</span> (you can also
                      type <code>\n</code>).
                    </p>
                    {/* Signature Preview */}
                    <div className="mb-4">
                      <label
                        htmlFor="signatureUpload"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Doctor's Signature
                      </label>
                      {signaturePreviewUrl && (
                        <div className="mt-2 border border-gray-300 rounded-md p-2 inline-block">
                          <img
                            src={signaturePreviewUrl}
                            alt="Signature Preview"
                            className="h-20 object-contain"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="sticky bottom-0 w-full bg-white p-4 border-t border-gray-200 shadow-md">
                  <div className="flex justify-end">
                    <DarkBlueButton
                      name="Preview"
                      handleClick={() => setSmallScreenPreview(true)}
                      type="button"
                      className="w-max h-12"
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="w-full p-4 bg-[#F7F0E8] overflow-y-auto h-[70vh] side_bar">
                <h1 className="text-[#3f4346] font-semibold mb-4">Preview</h1>
                <div className="flex justify-start md:justify-center">
                  <PrescriptionPdf
                    hospData={hospData}
                    booking={bookings}
                    docDetails={docDetails}
                    medicines={formData.medicines}
                    complaints={formData.complaints}
                    historyAndExaminations={formData.historyAndExaminations}
                    diagnosis={formData.diagnosis}
                    investigations={formData.investigations}
                    previousMedicines={formData.previousMedicines}
                    advice={formData.advice}
                    signatureImageUrl={signaturePreviewUrl || ""}
                    type="create"
                  />
                </div>
              </div>
            )
          ) : (
            // Large screen view
            <>
              <div
                className={
                  "w-full flex flex-col items-center justify-between p-6 md:p-4 border-r-[1px] border-[#d5d9dc] " +
                  (preview ? "hidden lg:flex !w-full lg:!w-full" : "")
                }
              >
                <div
                  className={`w-full ${
                    type === "view"
                      ? "h-[65vh]"
                      : "max-h-[45vh] xl:max-h-[50vh]"
                  } overflow-y-auto side_bar p-2 space-y-4`}
                >
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      Template Name
                    </label>
                    <input
                      type="text"
                      id="template_name"
                      name="template_name"
                      className="mt-1 w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="eg.cough,cold etc"
                      value={formData.template_name}
                      onChange={handleInputChange}
                      disabled={isView}
                    />
                  </div>
                  {FORM_FIELDS.map((field) => (
                    <React.Fragment key={field.name}>
                      <div key={field.name} className="mb-4">
                        <label
                          htmlFor={field.name}
                          className="block text-sm font-medium text-gray-700 mb-1"
                        >
                          {field.label}
                        </label>
                        <textarea
                          id={field.name}
                          name={field.name}
                          value={
                            formData[
                              field.name as keyof Omit<
                                TemplateData,
                                "medicines" | "vitals" | "header"
                              >
                            ]
                          }
                          onChange={handleInputChange}
                          className="mt-1 w-full p-2 border border-gray-300 rounded-md h-20 shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                          placeholder={field.placeholder}
                          disabled={isView}
                        />
                      </div>
                    </React.Fragment>
                  ))}

                  {/* Medicines Section */}
                  <div className="mb-4">
                    <label className="block text-lg font-semibold text-gray-800 mb-2">
                      Medicines
                    </label>
                    {formData.medicines.map((medicine, index) => (
                      <div
                        key={index}
                        className="p-3 border border-gray-200 rounded-md mb-3 bg-gray-50 shadow-sm"
                      >
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-2">
                          <div>
                            <label
                              htmlFor={`medicine_name_${index}`}
                              className="block text-xs font-medium text-gray-600 mb-1"
                            >
                              Name
                            </label>
                            <input
                              type="text"
                              id={`medicine_name_${index}`}
                              name="name"
                              value={medicine.name}
                              onChange={(e) => handleInputChange(e, index)}
                              onFocus={() => {
                                setSearchingIndex(index);
                                setSearch("");
                              }}
                              onBlur={() => {
                                setTimeout(() => {
                                  setSearch("");
                                  setSearchingIndex(null);
                                }, 200);
                              }}
                              className="mt-1 w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                              placeholder="e.g., Azithromycin"
                              disabled={isView}
                            />
                            {search !== "" && searchingIndex === index && (
                              <div className="px-0 rounded-lg bg-white relative shadow-lg mt-2 w-full z-20 max-h-60 overflow-y-auto overflow-x-hidden side_bar">
                                {isSearching ? (
                                  <div className="p-2 text-sm text-gray-500">
                                    Searching...
                                  </div>
                                ) : (
                                  searchResults.length > 0 &&
                                  searchResults.map((medData, ind) => (
                                    <div
                                      key={ind}
                                      className="flex gap-4 items-center hover:bg-queueHover hover:text-white rounded-md py-2 px-4 cursor-pointer"
                                      onClick={() => {
                                        setFormData((prev) => {
                                          const updatedMedicines = [
                                            ...prev.medicines,
                                          ];
                                          updatedMedicines[index] = {
                                            ...updatedMedicines[index],
                                            name: medData.product_name,
                                          };
                                          return {
                                            ...prev,
                                            medicines: updatedMedicines,
                                          };
                                        });
                                        setSearch("");
                                        setSearchingIndex(null);
                                      }}
                                    >
                                      <div>
                                        <p className="text-sm">
                                          {medData.product_name}
                                        </p>
                                      </div>
                                    </div>
                                  ))
                                )}
                              </div>
                            )}
                          </div>
                          <div>
                            <label
                              htmlFor={`medicine_dosage_${index}`}
                              className="block text-xs font-medium text-gray-600 mb-1"
                            >
                              Dosage
                            </label>
                            <input
                              type="text"
                              id={`medicine_dosage_${index}`}
                              name="dosage"
                              value={medicine.dosage}
                              onChange={(e) => handleInputChange(e, index)}
                              className="mt-1 w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                              placeholder="1-0-1 / 5ml"
                              disabled={isView}
                            />
                          </div>
                          <div>
                            <label
                              htmlFor={`medicine_duration_${index}`}
                              className="block text-xs font-medium text-gray-600 mb-1"
                            >
                              Duration
                            </label>
                            <input
                              type="text"
                              id={`medicine_duration_${index}`}
                              name="duration"
                              value={medicine.duration}
                              onChange={(e) => handleInputChange(e, index)}
                              className="mt-1 w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                              placeholder="e.g. 5 days , 2 weeks"
                              disabled={isView}
                            />
                          </div>
                        </div>
                        {!isView && (
                          <button
                            type="button"
                            onClick={() => removeMedicine(index)}
                            className="px-3 py-1 text-red-600 hover:text-red-800 text-xs font-medium rounded-md hover:bg-red-50 transition-colors duration-150"
                          >
                            Remove Medicine
                          </button>
                        )}
                      </div>
                    ))}
                    {!isView && (
                      <button
                        type="button"
                        onClick={addMedicine}
                        className="mt-2 px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-50 text-sm font-medium shadow-sm"
                      >
                        Add Medicine
                      </button>
                    )}
                  </div>

                  {/* Advice Textarea */}
                  <div className="mb-4">
                    <label
                      htmlFor="advice"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Advice
                    </label>
                    <textarea
                      id="advice"
                      name="advice"
                      value={formData.advice}
                      onChange={handleInputChange}
                      className="mt-1 w-full p-2 border border-gray-300 rounded-md h-20 shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="e.g., Take complete rest. Drink plenty of fluids. Follow up after 3 days."
                      disabled={isView}
                    />
                  </div>
                  {/* Disclaimer */}
                  <p className="mt-1 text-xs text-gray-500">
                    <span className="font-semibold">Note:</span> To make words
                    bold, wrap them in a single asterisk like{" "}
                    <code>*example*</code>. For a new line, press{" "}
                    <span className="font-semibold">Enter</span> (you can also
                    type <code>\n</code>).
                  </p>

                  {/* Signature Upload Section */}
                  <div className="mb-4">
                    <label
                      htmlFor="signatureUpload"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Doctor's Signature
                    </label>
                    {!isView && (
                      <input
                        type="file"
                        id="signatureUpload"
                        accept="image/*"
                        onChange={handleSignatureFileChange}
                        className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                      />
                    )}

                    {signaturePreviewUrl && (
                      <div className="mt-2 border border-gray-300 rounded-md p-2 inline-block">
                        <img
                          src={signaturePreviewUrl}
                          alt="Signature Preview"
                          className="h-20 object-contain"
                        />
                      </div>
                    )}
                    {isUploadingSignature && (
                      <p className="text-sm text-indigo-600 mt-1">
                        Uploading signature...
                      </p>
                    )}
                  </div>
                </div>
                {!isView && (
                  <div className=" px-2 w-full mt-4">
                    <label className="block text-sm font-medium">
                      Modify with <span className="text-blue-600">AI</span>
                    </label>
                    <div className="flex mt-1">
                      <input
                        value={aiPrompt}
                        onChange={(e) => setAiPrompt(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleGenerate();
                          }
                        }}
                        placeholder="e.g. Add data for cough and cold"
                        className="flex-1 p-2 border rounded"
                      />
                      <button
                        onClick={handleGenerate}
                        disabled={isGenerating}
                        className="ml-2 p-2 bg-gradient-to-r from-blue-500 to-purple-600 rounded disabled:opacity-50"
                      >
                        <BsStars size={20} color="#fff" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
              {(preview || type === "view") && (
                <div className="w-full md:p-6 bg-[#F7F0E8] overflow-y-auto h-[70vh] side_bar">
                  <h1
                    className={
                      "text-[#3f4346] font-semibold mb-4 " +
                      (preview ? "hidden md:flex !w-full lg:!w-full" : "")
                    }
                  >
                    Preview
                  </h1>
                  <div className="flex justify-start md:justify-center">
                    <PrescriptionPdf
                      hospData={hospData}
                      booking={bookings}
                      docDetails={docDetails}
                      medicines={formData.medicines}
                      complaints={formData.complaints}
                      historyAndExaminations={formData.historyAndExaminations}
                      diagnosis={formData.diagnosis}
                      investigations={formData.investigations}
                      previousMedicines={formData.previousMedicines}
                      advice={formData.advice}
                      signatureImageUrl={signaturePreviewUrl || ""}
                      type="create"
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        <div className="px-6 py-3 flex flex-row items-center justify-end border-t-[0.5px] border-sbBorder">
          <WhiteButton
            name={
              preview || (isSmallScreen && smallScreenPreview)
                ? "Close Preview"
                : "Close"
            }
            handleClick={() => {
              if (isSmallScreen && smallScreenPreview)
                setSmallScreenPreview(false);
              else if (preview) setPreview(!preview);
              else handleClose();
            }}
            className="w-max h-12"
          />
          {!isView && (
            <DarkBlueButton
              name={
                preview ? (type === "create" ? "Save" : "Update") : "Preview"
              }
              handleClick={() =>
                preview
                  ? type === "create"
                    ? handleSave()
                    : handleUpdate()
                  : setPreview(!preview)
              }
              type="button"
              className="w-max h-12"
              isLoading={loading}
            />
          )}
        </div>
      </div>
    </Modal>
  );
};

export default CreatePrescriptionModal;
